import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";

import MDTypography from "components/MDTypography";
// Custom styles for DashboardNavbar
import Breadcrumbs from "examples/Breadcrumbs";
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarDesktopMenu,
} from "examples/Navbars/DashboardNavbar/styles";

// Material Dashboard 3 PRO React context
import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
} from "context";

function DashboardNavbar({ absolute, light, isMini }) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const {
    transparentNavbar,
    fixedNavbar,
    darkMode,
  } = controller;
  const route = useLocation().pathname.split("/").slice(1);
  const location = useLocation();
  const navigate = useNavigate();
  
  useEffect(() => {
    if(window.screen.width <= 1280) {
      setMiniSidenav(dispatch, true);
    } // Ensure menu is always closed
  }, []);
  
  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      setTransparentNavbar(
        dispatch,
        (fixedNavbar && window.scrollY === 0) || !fixedNavbar
      );
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  // Styles for the navbar icons
  const iconsStyle = ({
    palette: { dark, white, text },
    functions: { rgba },
  }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;

      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      }

      return colorValue;
    },
  });

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) =>
        navbar(theme, { transparentNavbar, absolute, light, darkMode })
      }
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <MDBox color="inherit" sx={(theme) => navbarRow(theme, { isMini })}>
          <IconButton
            sx={navbarDesktopMenu}
            onClick={() => {
              setMiniSidenav(dispatch, true); // Allow sidenav popup for the homepage
              navigate("/"); //navigate to the homepage
            }}
            size="small"
            disableRipple
          >
            <Icon fontSize="medium" sx={iconsStyle}>
              menu
            </Icon>
          </IconButton>
          <MDTypography
            variant="h5"
            fontWeight="medium"
            sx={{
              cursor: "pointer",
              ml: 1, // Add some margin between the icon and the text
              color: "#6c757d", // Slightly gray color to match the icon
            }}
            onClick={() => {
              setMiniSidenav(dispatch, false); // Allow sidenav popup for the homepage
              // navigate("/"); //navigate to the homepage
            }}
          >
            Solar Sentiment
          </MDTypography>
          <MDTypography
            variant="h5"
            fontWeight="medium"
            sx={{
              ml: 0.5, // Add margin left to the slash
              mr: 0.25, // Add margin right to the slash
              color: "#6c757d", // Slightly gray color to match the icon// Match the icon's color
            }}
          >
            /
          </MDTypography>
          <Breadcrumbs
            icon="home"
            title={route[0]} // Show only the first level
            route={route.slice(0, 1)} // Pass only the first level for the route
            light={light}
            sx={{
              fontSize: "1.25rem", // Match h5 size
              fontWeight: "medium", // Match h5 font weight
            }}
          />
        </MDBox>
      </Toolbar>
    </AppBar>
  );
}

// Setting default values for the props of DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;